import React, { useMemo } from 'react'
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import styled from 'styled-components'
import {
  isEmpty,
  keys,
  filter,
  toPairs,
  capitalize,
  includes,
  map,
  orderBy,
} from 'lodash'
import { useTranslation } from 'react-i18next'

import {
  Props,
  SentimentStateProps,
  renderEmptyState,
  renderSentimentSkeletons,
  SENTIMENT_COLORS,
} from './Helpers/SentimentHelper'
import { useAppSelector } from 'redux/Hook'

const StyledCard = styled(Card)`
  width: 100%;
  border-radius: 0.75rem;
`

const StyledCardContent = styled(CardContent)`
  width: 100%;
  padding: 1.5rem;

  @media only screen and (max-width: 700px) {
    padding: 1rem;
  }
`

const StyledLinearProgress = styled(LinearProgress)<{ $barcolor: string }>`
  flex-grow: 1;
  margin-right: 1rem;
  background-color: ${grey[300]};
  border-radius: 3px;

  & .MuiLinearProgress-bar1Determinate {
    background-color: ${({ $barcolor }) => $barcolor || grey[300]};
  }

  max-width: 400px;
`

const SentimentSummary = ({ title, sentiment }: Props) => {
  const { t } = useTranslation()
  const {
    appConfig: { platformSentiment, sentimentToggle, doneAnalysis },
  } = useAppSelector((state) => state.textAnalysis.main)

  const { pending, done, error, data }: SentimentStateProps = sentiment
  const loading = pending && !done && !error
  const showSkeleton = !doneAnalysis || loading

  const sentimentData = useMemo(() => {
    if (!data || isEmpty(data)) return []

    const sentimentKeys = keys(SENTIMENT_COLORS)

    const filteredData = map(
      filter(toPairs(data), ([key]) =>
        includes(sentimentKeys, capitalize(key)),
      ),
      ([key, value]) => ({
        key: capitalize(key),
        value: value as number,
        order: SENTIMENT_COLORS[capitalize(key)].order,
        percentage: ((value / (data?.count || 1)) * 100).toFixed(1),
      }),
    )

    return orderBy(filteredData, ['order'], ['asc'])
  }, [data])

  const renderSentimentData = () =>
    sentimentData.map(({ key, value, percentage }: any, index) => {
      return (
        <Box
          key={key}
          display="flex"
          alignItems="center"
          mb={index === sentimentData.length - 1 ? 0 : 1}
        >
          <Typography variant="body2" sx={{ minWidth: { xs: 70, sm: 100 } }}>
            {t(`textAnalysis.sentiment.${key.toLowerCase()}`)}
          </Typography>
          <Typography variant="body2" sx={{ minWidth: { xs: 20, sm: 25 } }}>
            {value}
          </Typography>

          <StyledLinearProgress
            variant="determinate"
            value={parseFloat(percentage)}
            aria-hidden
            $barcolor={SENTIMENT_COLORS[key]?.color}
          />
          <Typography variant="body2">{percentage}%</Typography>
        </Box>
      )
    })

  const renderContent = () => {
    if (showSkeleton) return renderSentimentSkeletons()
    if (!platformSentiment) return renderEmptyState('disableAnalysis')
    if (!sentimentToggle) return renderEmptyState('disableSentiment')
    if (!sentimentData.length) return renderEmptyState()
    return renderSentimentData()
  }

  return (
    <StyledCard elevation={0} data-testid="ta-sentiment-summary">
      <StyledCardContent>
        <Typography mb={2}>{title || ''}</Typography>
        {renderContent()}
      </StyledCardContent>
    </StyledCard>
  )
}

export default React.memo(SentimentSummary)
